import ApiClient, { LOGIN_LONG_URL, LOGIN_URL } from "../../common/API";
import { useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import LoadingComponent from "../../common/components/LoadingComponent";
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import EmailIcon from '@mui/icons-material/Email';
import IconButton from '@mui/material/IconButton';
import * as React from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import { Grid } from "@mui/material";
import { LoginContext } from "../../common/context/LoginContext";
import { isDefaultDevice, splitDefaultDevice } from "./MultiFactorAuthentication/utils";
import MicrosoftLogin from "./MicrosoftLogin";
import { showToast }from "../../common/components/ToastComponent";
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Collapse from "@mui/material/Collapse";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { FormControlLabel, Checkbox, } from '@mui/material';

export default function LoginForm(props) {
    let navigate = useNavigate();
    let location = useLocation();
    let from = location.state?.from?.pathname || "/";

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [checked, setChecked] = useState(true);
    const [showPasswordLogin, setShowPasswordLogin] = useState(true);

    const handleChange = (event) => {
        setChecked(event.target.checked);
    };
    const params = {
        username: props.email,
        password: props.password,
        is_long_life: props.rememberMe
    }

    const { setLoginDetails } = React.useContext(LoginContext)

    const login = (e) => {
        if (e) {
            e.preventDefault();
        }
        setIsLoading(true);

        ApiClient().post('/auth/', params).then(response => {
            setIsLoading(false);
            setIsError(false);
            props.setErrorMessage('')

            const canChangePassword = response.data?.can_change_password || false;
            const requirePasswordChange = response.data?.require_password_change || false;

            setLoginDetails({
                canChangePassword: canChangePassword,
                requirePasswordChange: canChangePassword && requirePasswordChange,
                username: response.data?.username,
            })
            const tokens = {
                'accessToken': response.data.access,
                'refreshToken': response.data.refresh
            }

            const defaultDevice = response?.data?.default_device
            const newDefaultDevice = splitDefaultDevice(defaultDevice)
            const activeTab = isDefaultDevice(newDefaultDevice)
            const mfaDisabled = response?.data?.devices?.length > 1

            if (response.data.hasOwnProperty('code')) { // check if MFA enabled
                props.setActiveTab(activeTab)
                props.setCode(response.data.code)
                props.setDefaultDevice(newDefaultDevice)
                props.setDisabled(mfaDisabled)
                props.setLockUntil(response.data?.default_device_details?.locked_until);
            } else {
                localStorage.setItem('auth-tokens-production', JSON.stringify(tokens));
                localStorage.setItem("remember_me", props.rememberMe);
                // Send them back to the page they tried to visit when they were
                // redirected to the login page. Use { replace: true } so we don't create
                // another entry in the history stack for the login page.  This means that
                // when they get to the protected page and click the back button, they
                // won't end up back on the login page, which is also really nice for the
                // user experience. 

                 if (response.data?.auth_method === "linotp") {
                    showToast(
                            "warning",
                            "Login Update",
                            'ADID+password login will be discontinued soon. Please use the "Sign in with Microsoft" button for future logins.'
                        );
                } else {
                    showToast(
                        "info",
                        "Recommended Login Method",
                        'If you use an ADID, password logins are deprecating soon. We highly recommend switching to the "Sign in with Microsoft" button.'
                    );
                }
                
                navigate(from, { replace: true });
            }

        }).catch(error => {
            const errorMsg = error?.response?.data ? error.response.data?.detail : error.message;
            setIsLoading(false);
            setIsError(true);
            showToast(
                "error",
                "Login Failed",
                `${errorMsg}. If you are using an ADID, this method may no longer be supported. Please try the "Sign in with Microsoft" button.`
            );
        });
    };

    const [showPassword, setShowPassword] = React.useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    return (
        <Box
            component="form"
            noValidate
            autoComplete="off"
        >
        <MicrosoftLogin />
           <Box
                onClick={() => setShowPasswordLogin(prev => !prev)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    cursor: "pointer",
                    my: 2,
                }}
            >
                <Divider sx={{ flex: 1, borderColor:"gray"}} />
                <Box
                    sx={{
                        mx: 1.2,
                        fontSize: "1rem",
                        color: "gray",
                        whiteSpace: "nowrap",
                    }}
                >
                    Sign In via Password
                </Box>

                <Divider sx={{ flex: 1, borderColor:"gray"}} />

                {showPasswordLogin ? (
                    <KeyboardArrowUpIcon
                        sx={{
                            ml: 1,
                            fontSize: "1.5rem",
                            color: "gray",
                        }}
                    />
                ) : (
                    <KeyboardArrowDownIcon
                        sx={{
                            ml: 1,
                            fontSize: "1.5rem",
                            color: "gray",
                        }}
                    />
                )}
            </Box>
            <Collapse in={showPasswordLogin} timeout="auto">
                <Alert
                    severity="warning"
                    sx={{
                        mb: 2,
                        textAlign: "left"
                    }}
                >
                    <strong>Notice:</strong>{" "}
                    ADID + Password login is deprecating soon.
                    Please use "Sign in with Microsoft".
                </Alert>
                <TextField
                    InputProps={{
                        startAdornment: <EmailIcon sx={{ color: 'primary.main', mr: 1, my: 0.5 }} />,
                    }}
                    required
                    id="outlined"
                    label="Username / Email"
                    type="text"
                    placeholder="Enter Your Email or Username"
                    fullWidth
                    sx={{ my: 1, width: "100%" }}
                    onChange={(e) => props.setEmail(e.target.value)} value={props.email}
                />
                <TextField sx={{ my: 1, mt: "15px", width: "100%" }}
                    required
                    id="outlined"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter Your Password"
                    InputProps={{
                        startAdornment:
                            < LockIcon sx={{ color: 'primary.main', mr: 1, my: 0.5 }} />,
                        endAdornment:
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>,
                    }}
                    onChange={(e) => props.setPassword(e.target.value)} value={props.password}
                >
                </TextField>
                <Grid textAlign='center' sx={{ my: 1 }}>
                    {
                        isLoading ? <LoadingComponent /> :
                            <Button
                                type="submit"
                                startIcon={<LoginIcon />}
                                size="large"
                                variant="contained"
                                disabled={props.disabled}
                                onClick={() => { login() }}
                                sx={{ my: 1, width: "100%", fontFamily: "Henry Sans", variant: "h6" }}>Sign in
                            </Button>
                    }
                </Grid>

                <Box sx={{ width: '100%', display: "flex", justifyContent: "end" }}>
                    <FormControlLabel
                        value="start"
                        control={<Checkbox
                            onChange={() => props.setRememberMe(!props.rememberMe)}
                            checked={props.rememberMe}
                        />}
                        label="Remember me"
                        labelPlacement="start"
                    />
                </Box>
                <Box
                    sx={{
                        typography: "subtitle1",
                        textAlign: "center",
                        mt: "10px"
                    }}
                    style={{
                        fontFamily: "Henry Sans",
                    }}
                >
                    Don't have an account?
                    <Button
                        size="large"
                        variant="button"
                        label="Login"
                        onClick={props.switchToRegisterForm}// Switch back to registration form*** function set at index
                        style={{
                            cursor: "pointer",
                            fontWeight: "bold",
                            color: "#1976d2",
                            textTransform: "none",
                        }}>{'Register Here'}
                    </Button>
                </Box>
            </Collapse>
        </Box>
    )
}